import { ReactNode, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useResponsive from 'src/hooks/useResponsive';
import LoginHeader from 'src/layouts/login/LoginHeader';
import AuthFlowWelcome from './AuthFlowWelcome';
import { AUTH_FLOW_ARTWORK, AuthFlowScreen } from './content';
import { FlowCard, FlowColumn, FlowMain, FlowRoot } from './styles';

interface AuthFlowLayoutProps {
  screen: AuthFlowScreen;
  children: ReactNode;
  title?: ReactNode;
  wide?: boolean;
}

export default function AuthFlowLayout({
  screen,
  children,
  title,
  wide = false,
}: AuthFlowLayoutProps): JSX.Element {
  const { t } = useTranslation();
  const isDesktop = useResponsive('up', 'lg');
  const [readyArtwork, setReadyArtwork] = useState<string | null>(null);
  const [hasFormInteraction, setHasFormInteraction] = useState(false);
  const artwork = AUTH_FLOW_ARTWORK[screen].src;

  return (
    <FlowRoot data-testid="auth-flow-page" data-screen={screen}>
      <LoginHeader />
      <FlowMain
        data-testid="auth-flow-main"
        sx={{
          '--welcome-animation-play-state':
            !isDesktop || readyArtwork === artwork ? 'running' : 'paused',
          ...(wide && {
            gridTemplateColumns: { lg: 'minmax(0, 0.8fr) minmax(0, 1.2fr)' },
          }),
        }}
      >
        {isDesktop && (
          <AuthFlowWelcome
            key={screen}
            screen={screen}
            wide={wide}
            onArtworkReady={() => setReadyArtwork(artwork)}
          />
        )}
        <FlowColumn aria-labelledby="auth-flow-title">
          <FlowCard
            data-testid="auth-flow-card"
            onFocusCapture={() => setHasFormInteraction(true)}
            sx={{
              ...(wide && { maxWidth: (theme) => theme.spacing(112) }),
              ...(hasFormInteraction && { animation: 'none' }),
            }}
          >
            <Typography
              id="auth-flow-title"
              component="h1"
              variant="h4"
              sx={{ fontWeight: 800, lineHeight: 1.3, mb: 3 }}
            >
              {title ?? t(`authFlow.${screen}.title`)}
            </Typography>
            {children}
          </FlowCard>
        </FlowColumn>
      </FlowMain>
    </FlowRoot>
  );
}
